// Service Worker Registration e Push Notifications

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Cache para a chave VAPID
let vapidPublicKey = null;

// Obter chave VAPID do servidor
async function getVapidPublicKey() {
  if (vapidPublicKey) return vapidPublicKey;
  
  try {
    const response = await fetch(`${API}/notifications/vapid-public-key`);
    if (response.ok) {
      const data = await response.json();
      vapidPublicKey = data.publicKey;
      return vapidPublicKey;
    }
  } catch (error) {
    console.error('Error fetching VAPID key:', error);
  }
  
  return null;
}

// Registar Service Worker
export async function registerServiceWorker() {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/'
      });
      
      console.log('Service Worker registado com sucesso:', registration.scope);
      
      // Verificar atualizações
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        console.log('Nova versão do Service Worker encontrada');
        
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            // Nova versão disponível
            if (window.confirm('Nova versão disponível! Recarregar para atualizar?')) {
              window.location.reload();
            }
          }
        });
      });
      
      return registration;
    } catch (error) {
      console.error('Erro ao registar Service Worker:', error);
      return null;
    }
  }
  return null;
}

// Verificar suporte a notificações
export function supportsNotifications() {
  return 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
}

// Pedir permissão para notificações
export async function requestNotificationPermission() {
  if (!supportsNotifications()) {
    console.log('Notificações não suportadas neste browser');
    return false;
  }
  
  const permission = await Notification.requestPermission();
  return permission === 'granted';
}

// Obter estado da permissão
export function getNotificationPermission() {
  if (!supportsNotifications()) return 'unsupported';
  return Notification.permission;
}

// Subscrever push notifications
export async function subscribeToPushNotifications(userId) {
  try {
    const registration = await navigator.serviceWorker.ready;
    
    // Verificar se já está subscrito
    let subscription = await registration.pushManager.getSubscription();
    
    if (!subscription) {
      // Obter chave VAPID do servidor
      const publicVapidKey = await getVapidPublicKey();
      
      if (!publicVapidKey) {
        console.error('VAPID public key not available');
        return null;
      }
      
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicVapidKey)
      });
      
      // Enviar subscrição para o servidor
      const subscriptionJson = subscription.toJSON();
      
      await fetch(`${API}/notifications/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('mes_token')}`
        },
        body: JSON.stringify({
          user_id: userId,
          subscription: {
            endpoint: subscriptionJson.endpoint,
            keys: subscriptionJson.keys,
            expirationTime: subscriptionJson.expirationTime
          }
        })
      });
      
      console.log('Subscrito a push notifications');
    }
    
    return subscription;
  } catch (error) {
    console.error('Erro ao subscrever push notifications:', error);
    return null;
  }
}

// Cancelar subscrição
export async function unsubscribeFromPushNotifications() {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    
    if (subscription) {
      await subscription.unsubscribe();
      console.log('Subscrição cancelada');
    }
    
    return true;
  } catch (error) {
    console.error('Erro ao cancelar subscrição:', error);
    return false;
  }
}

// Mostrar notificação local
export function showNotification(title, options = {}) {
  if (getNotificationPermission() !== 'granted') {
    console.log('Permissão de notificação não concedida');
    return;
  }
  
  const defaultOptions = {
    icon: '/icons/icon-192x192.png',
    badge: '/icons/icon-72x72.png',
    vibrate: [200, 100, 200],
    tag: 'ep3d-notification',
    renotify: true,
    requireInteraction: false,
    ...options
  };
  
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    // Usar service worker para mostrar notificação
    navigator.serviceWorker.ready.then((registration) => {
      registration.showNotification(title, defaultOptions);
    });
  } else {
    // Fallback para Notification API
    new Notification(title, defaultOptions);
  }
}

// Converter VAPID key
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  
  return outputArray;
}

// Verificar se app está instalada como PWA
export function isInstalledAsPWA() {
  return window.matchMedia('(display-mode: standalone)').matches ||
         window.navigator.standalone === true ||
         document.referrer.includes('android-app://');
}

// Prompt para instalar PWA
let deferredPrompt = null;

export function setupInstallPrompt() {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e;
    console.log('PWA pode ser instalada');
  });
}

export async function promptInstall() {
  if (!deferredPrompt) {
    console.log('Prompt de instalação não disponível');
    return false;
  }
  
  deferredPrompt.prompt();
  const { outcome } = await deferredPrompt.userChoice;
  
  console.log('Resultado da instalação:', outcome);
  deferredPrompt = null;
  
  return outcome === 'accepted';
}

export function canPromptInstall() {
  return deferredPrompt !== null;
}

// Esconder splash screen
export function hideSplashScreen() {
  const splash = document.getElementById('splash-screen');
  if (splash) {
    splash.classList.add('hidden');
    setTimeout(() => {
      splash.style.display = 'none';
    }, 500);
  }
}

// Inicializar PWA
export async function initPWA() {
  // Registar service worker
  const registration = await registerServiceWorker();
  
  // Configurar prompt de instalação
  setupInstallPrompt();
  
  // Registar periodic background sync para keep-alive
  if (registration) {
    registerKeepAlive(registration);
  }
  
  // Esconder splash após carregamento
  if (document.readyState === 'complete') {
    hideSplashScreen();
  } else {
    window.addEventListener('load', hideSplashScreen);
  }
  
  console.log('PWA inicializada');
}

// Registar periodic sync para manter servidor acordado com app fechada
async function registerKeepAlive(registration) {
  try {
    // Periodic Background Sync (mantém a funcionar mesmo com app fechada)
    if ('periodicSync' in registration) {
      const status = await navigator.permissions.query({ name: 'periodic-background-sync' });
      if (status.state === 'granted') {
        await registration.periodicSync.register('keep-alive', {
          minInterval: 2 * 60 * 1000, // 2 minutos
        });
        console.log('[PWA] Periodic sync keep-alive registado');
      }
    }
    
    // Fallback: Background Sync simples (executa quando volta online)
    if ('sync' in registration) {
      await registration.sync.register('keep-alive');
    }
  } catch (e) {
    console.log('[PWA] Background sync não suportado:', e.message);
  }
}
